"use client";

import styles from "./IngridTracking.module.css";
import BlokWrap from "@/components/bloks/BlokWrap";
import { useGeo } from "@/i18n/routing";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { useEffect, useRef, useState, Suspense, useCallback } from "react";

import type { BlokIngridTracking } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";

const INGRID_SCRIPT_SRC = "https://cdn.ingrid.com/delivery-tracking/bootstrap.js";
const CONTAINER_ID = "delivery-tracking-widget";
const SITE_ID = "a0aa3523-2471-4c98-8fbe-8d586ef66011";

function loadIngridScript(): Promise<void> {
	return new Promise((resolve, reject) => {
		const existing = document.querySelector(`script[src="${INGRID_SCRIPT_SRC}"]`);
		if (existing) {
			existing.remove();
		}

		const script = document.createElement("script");
		script.src = INGRID_SCRIPT_SRC;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () => reject();
		document.body.appendChild(script);
	});
}

const renderIngridWidget = ({
	identifier,
	contact,
	geo,
}: {
	identifier?: string;
	contact?: string;
	geo: string;
}) => {
	if (!window?.IngridDeliveryTrackingWidgetApi) return;

	const config = {
		containerId: CONTAINER_ID,
		locale: geo,
		siteId: SITE_ID,
	};

	if (identifier && contact) {
		window.IngridDeliveryTrackingWidgetApi.renderPrivateMode({
			...config,
			identifier,
			contact,
		});
	} else {
		window.IngridDeliveryTrackingWidgetApi.renderSearchMode(config);
	}
};

const UnsuspendedIngridTrackingBlok = ({
	blok,
	geo,
}: BlokProps<BlokIngridTracking> & { geo: string }) => {
	const ref = useRef<HTMLDivElement>(null);
	const searchParams = useUrlSearchParams();
	const [scriptLoaded, setScriptLoaded] = useState(false);

	const identifier = searchParams.get("identifier") ?? undefined;
	const contact = searchParams.get("contact") ?? undefined;

	// Reload the script every time `geo` changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setScriptLoaded(false);
		loadIngridScript()
			.then(() => setScriptLoaded(true))
			.catch(() => console.error("Failed to load Ingrid script"));
	}, [geo]);

	useEffect(() => {
		if (!scriptLoaded || !ref.current) return;

		ref.current.innerHTML = "";
		renderIngridWidget({ identifier, contact, geo });
	}, [scriptLoaded, identifier, contact, geo]);

	return (
		<BlokWrap editable={blok} as="div" className={styles.root}>
			<div className={styles.container}>
				<h2 className={styles.title}>{blok.title}</h2>
				<div ref={ref} id={CONTAINER_ID} />
			</div>
		</BlokWrap>
	);
};

export const IngridTrackingBlok = (props: BlokProps<BlokIngridTracking>) => {
	const { geo } = useGeo();

	return (
		<Suspense>
			<UnsuspendedIngridTrackingBlok {...props} geo={geo} />
		</Suspense>
	);
};
